import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FileText, PackagePlus, Search, UserPlus, Radio, Zap } from 'lucide-react';
import { clientsAPI, serviceCallsAPI } from '@shared/apiClient';
import { getClientDisplayName } from '@shared/utils';
import toast from 'react-hot-toast';
import ClientDetailsModal from '../components/ClientDetailsModal';
import ServiceCallModal from '../components/ServiceCallModal';
import PartsNeededFormModal from '../components/PartsNeededFormModal';
import NewClientModal from '../components/NewClientModal';
import CSpireSitesTab from '../components/CSpireSitesTab';
import FranklinSitesTab from '../components/FranklinSitesTab';

const getClientListName = (client) => {
  if (!client) return 'Unknown Customer';
  if (client.isCommercial && client.companyName) return client.companyName;
  const last = client.contactLastName || '';
  const first = client.contactFirstName || '';
  const name = [last, first].filter(Boolean).join(', ').trim();
  return name || getClientDisplayName(client);
};

const getClientLocation = (client) => {
  if (!client) return '—';
  const locationParts = [client.city, client.state].filter(Boolean).join(', ');
  return locationParts || client.address || '—';
};

const TABS = [
  { id: 'clients', label: 'Clients', icon: UserPlus },
  { id: 'cspire', label: 'C Spire Sites', icon: Radio },
  { id: 'franklin', label: 'Franklin Sites', icon: Zap },
];

export default function Clients() {
  const [activeTab, setActiveTab] = useState('clients');
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedServiceCall, setSelectedServiceCall] = useState(null);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isPartsModalOpen, setIsPartsModalOpen] = useState(false);
  const [isNewClientOpen, setIsNewClientOpen] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading } = useQuery(
    ['clients', page, searchTerm],
    () => clientsAPI.getClients({
      page,
      limit: 50,
      search: searchTerm || undefined,
      isActive: true
    }),
    { keepPreviousData: true, enabled: activeTab === 'clients' }
  );

  const createServiceCallMutation = useMutation(
    (clientId) => serviceCallsAPI.createTechnicianServiceCall({ clientId }),
    {
      onSuccess: (response) => {
        const createdServiceCall = response?.data?.serviceCall;
        if (!createdServiceCall) {
          toast.error('Service call created, but no details returned.');
          return;
        }
        setSelectedServiceCall(createdServiceCall);
        setIsServiceModalOpen(true);
        toast.success('Service call started');
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('priorityAssignments');
        queryClient.invalidateQueries('myServiceCalls');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create service call');
      }
    }
  );

  const clients = data?.data?.clients || [];
  const pagination = data?.data?.pagination;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">View Clients</h1>
          <p className="mt-1 text-sm text-gray-500">
            Browse all clients and view customer details
          </p>
        </div>
        {activeTab === 'clients' && (
          <div className="flex gap-2">
            <button
              onClick={() => setIsNewClientOpen(true)}
              className="btn bg-white border border-primary-300 text-primary-700 hover:bg-primary-50"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              New Customer
            </button>
            <button
              onClick={() => setIsPartsModalOpen(true)}
              className="btn btn-primary"
            >
              <PackagePlus className="h-4 w-4 mr-2" />
              Request Part
            </button>
          </div>
        )}
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex -mb-px gap-2">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-5 text-base font-semibold border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="inline h-4 w-4 mr-1" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'clients' && (
        <>
          <div className="bg-white shadow rounded-lg">
            <div className="p-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            {isLoading ? (
              <div className="text-center py-10 text-gray-500">Loading clients...</div>
            ) : clients.length === 0 ? (
              <div className="text-center py-10 text-gray-500">No clients found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clients.map((client) => (
                      <tr key={client.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {getClientListName(client)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {getClientLocation(client)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <div className="flex items-center justify-end gap-4">
                            <button
                              onClick={() => {
                                setSelectedClient(client);
                                setIsDetailsOpen(true);
                              }}
                              className="text-primary-600 hover:text-primary-900 font-medium"
                            >
                              View Details
                            </button>
                            <button
                              onClick={() => createServiceCallMutation.mutate(client.id)}
                              disabled={createServiceCallMutation.isLoading}
                              className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Service Call
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {pagination && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={!pagination || pagination.page <= 1}
                  className="btn btn-secondary btn-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={!pagination || pagination.page >= pagination.pages}
                  className="btn btn-secondary btn-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          <ClientDetailsModal
            client={selectedClient}
            isOpen={isDetailsOpen}
            onClose={() => {
              setIsDetailsOpen(false);
              setSelectedClient(null);
            }}
          />

          {selectedServiceCall && (
            <ServiceCallModal
              serviceCall={selectedServiceCall}
              isOpen={isServiceModalOpen}
              onClose={() => {
                setIsServiceModalOpen(false);
                setSelectedServiceCall(null);
              }}
            />
          )}

          <PartsNeededFormModal
            isOpen={isPartsModalOpen}
            onClose={() => setIsPartsModalOpen(false)}
          />

          <NewClientModal
            isOpen={isNewClientOpen}
            onClose={() => setIsNewClientOpen(false)}
            onCreated={(newClient) => {
              queryClient.invalidateQueries('clients');
            }}
          />
        </>
      )}

      {activeTab === 'cspire' && <CSpireSitesTab />}
      {activeTab === 'franklin' && <FranklinSitesTab />}
    </div>
  );
}
